import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import Decimal from 'decimal.js';
import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../util/currency';

const { Money } = sdkTypes;

const VoucherStepComponent = ({
  onApplyVoucher,
  voucherValidationInProgress,
  voucherValidationError,
  appliedVoucher,
}) => {
  const intl = useIntl();
  const [voucherCode, setVoucherCode] = useState('');

  const voucherLabel = intl.formatMessage({ id: 'BookingDatesForm.voucherLabel' });
  const voucherPlaceholder = intl.formatMessage({ id: 'BookingDatesForm.voucherPlaceholder' });
  const applyButtonLabel = intl.formatMessage({ id: 'BookingDatesForm.applyVoucherButton' });
  const voucherAppliedLabel = intl.formatMessage({ id: 'BookingDatesForm.voucherAppliedLabel' });

  const errorKeyMap = {
    voucher_not_found: 'BookingDatesForm.voucherNotFound',
    voucher_not_valid: 'BookingDatesForm.voucherNotValid',
    voucher_not_applicable: 'BookingDatesForm.voucherNotApplicable',
    voucher_holder_id_mismatch: 'BookingDatesForm.voucherHolderMismatch',
    redemption_does_not_match_validation_rules:
      'BookingDatesForm.voucherRedemptionNotMatchValidationRules',
  };

  const getErrorMessage = () => {
    if (!voucherValidationError) return undefined;
    const translationKey = errorKeyMap[voucherValidationError.key];
    if (translationKey) return intl.formatMessage({ id: translationKey });
    // Only Voucherify's domain `reason` may surface raw; `message` can carry
    // technical transport errors (util/api) that must not reach customers.
    return (
      voucherValidationError.reason ||
      intl.formatMessage({ id: 'BookingDatesForm.voucherNotValid' })
    );
  };

  const handleApply = () => {
    if (voucherCode.trim() && onApplyVoucher) {
      onApplyVoucher(voucherCode.trim());
    }
  };

  // Voucher already applied - show confirmation
  if (appliedVoucher?.valid) {
    const isGiftCard = appliedVoucher.type === 'voucher' && appliedVoucher.gift != null;
    const appliedAmount = isGiftCard
      ? (appliedVoucher.order?.total_applied_discount_amount ?? 0)
      : 0;
    const remainingBalance = isGiftCard ? appliedVoucher.gift.balance - appliedAmount : 0;

    const formattedApplied = isGiftCard
      ? formatMoney(intl, new Money(new Decimal(appliedAmount).round().toNumber(), 'EUR'))
      : null;
    const formattedRemaining = isGiftCard
      ? formatMoney(intl, new Money(new Decimal(remainingBalance).round().toNumber(), 'EUR'))
      : null;

    return (
      <Stack direction="column" spacing={1}>
        <Typography color="success.main" variant="body2">
          {voucherAppliedLabel}: <strong>{appliedVoucher.code}</strong>
        </Typography>
        {appliedVoucher.discount ? (
          <Typography color="text.secondary" variant="caption">
            {appliedVoucher.discount.type === 'PERCENT'
              ? `-${appliedVoucher.discount.percent_off}%`
              : `-${appliedVoucher.discount.amount_off / 100} ${appliedVoucher.discount.currency || 'EUR'}`}
          </Typography>
        ) : null}
        {isGiftCard ? (
          <>
            <Typography color="text.secondary" variant="caption">
              {intl.formatMessage(
                { id: 'BookingDatesForm.giftCardAppliedAmount' },
                { amount: formattedApplied }
              )}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {intl.formatMessage(
                { id: 'BookingDatesForm.giftCardRemainingBalance' },
                { amount: formattedRemaining }
              )}
            </Typography>
          </>
        ) : null}
      </Stack>
    );
  }

  // No annual pass - show voucher input
  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="body2">{voucherLabel}</Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          disabled={voucherValidationInProgress}
          error={Boolean(voucherValidationError)}
          fullWidth
          helperText={getErrorMessage()}
          onChange={(e) => setVoucherCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
          placeholder={voucherPlaceholder}
          size="small"
          value={voucherCode}
        />
        <Box>
          <Button
            disabled={!voucherCode.trim() || voucherValidationInProgress}
            onClick={handleApply}
            variant="outlined"
          >
            {voucherValidationInProgress ? <CircularProgress size={20} /> : applyButtonLabel}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};

const VoucherStep = memo(VoucherStepComponent);

export default VoucherStep;
